import type { Seals } from "gql/api";
import type { Maybe } from "graphql/jsutils/Maybe";
import type { SealExpansionReturn } from "./types";

export function getLowerTandemSealData(
  primarySeal: Maybe<Partial<Seals>>,
  bottomSeal: Maybe<Partial<Seals>>,
  motorOilCoeff: number,
  gradientFPerFt: number,
  motorDepthFt: number,
  lowerSealDepthFt: number,
  topSealDepthFt: number,
  motorOilVolumeGal: number,
  surfaceTempF: number,
  motorSkinTempF: number,
  motorWindingTempF: number
): SealExpansionReturn {
  // If we have two seals, this the primary seal is the upper seal
  // and bottom seal is the lower one. If we only have one seal,
  // then the primary seal is both the upper and lower seal. The
  // calcs that need followed in that case are the lower tandem
  // exp calcs for the lower seal (since it's still the primary one)
  let upperTandemSeal;
  let lowerTandemSeal;
  // Single seal
  if (!bottomSeal) {
    upperTandemSeal = null;
    lowerTandemSeal = primarySeal;
  } else {
    // Two seals
    upperTandemSeal = primarySeal;
    lowerTandemSeal = bottomSeal;
  }
  // INSTALL
  // If no upper seal then these are 0
  // F54
  const installUpperLowerTandemTempDeltaF = !upperTandemSeal
    ? 0
    : topSealDepthFt * gradientFPerFt;
  // G54
  const installUpperLowerTandemVolumeForExpansionGal = !upperTandemSeal
    ? 0
    : (lowerTandemSeal?.bag_chambers || 0) > 0
      ? 0
      : upperTandemSeal?.volume_for_bag_expansion_upper_tandem_gallons;
  // H54
  const installUpperLowerTandemAmountOfExpansionGal =
    installUpperLowerTandemVolumeForExpansionGal *
    installUpperLowerTandemTempDeltaF *
    motorOilCoeff;
  // F55
  const installLowerLowerTandemTempDeltaF = lowerSealDepthFt * gradientFPerFt;
  // G55
  const installLowerLowerTandemVolumeForExpansionGal =
    lowerTandemSeal?.volume_for_bag_expansion_lower_tandem_gallons || 0;
  // H55
  const installLowerLowerTandemAmountOfExpansionGal =
    installLowerLowerTandemVolumeForExpansionGal *
    installLowerLowerTandemTempDeltaF *
    motorOilCoeff;
  // I56
  const installLowerTandemTotalSealExpansionGal =
    installUpperLowerTandemVolumeForExpansionGal +
    installLowerLowerTandemVolumeForExpansionGal;
  // F56
  const installLowerTandemMotorTempDeltaF = motorDepthFt * gradientFPerFt;
  // G56
  const installLowerTandemMotorVolumeForExpansionGal = motorOilVolumeGal;
  // H56
  const installLowerTandemMotorAmountOfExpansionGal =
    installLowerTandemMotorVolumeForExpansionGal *
    installLowerTandemMotorTempDeltaF *
    motorOilCoeff;
  // H57
  const installLowerTandemTotalAmountOfExpansionGal =
    installUpperLowerTandemAmountOfExpansionGal +
    installLowerLowerTandemAmountOfExpansionGal +
    installLowerTandemMotorAmountOfExpansionGal;
  // I57
  const installLowerTandemTotalExpansionGal =
    installLowerTandemTotalAmountOfExpansionGal >
    installLowerTandemTotalSealExpansionGal
      ? installLowerTandemTotalSealExpansionGal
      : installLowerTandemTotalAmountOfExpansionGal;
  // J57
  const installLowerTandemExpansionRatio =
    installLowerTandemTotalAmountOfExpansionGal /
    installLowerTandemTotalSealExpansionGal;

  // OPERATION
  // If no upper seal then these are 0
  // F61
  const operationUpperLowerTandemTempDeltaF = !upperTandemSeal
    ? 0
    : motorSkinTempF - installUpperLowerTandemTempDeltaF - surfaceTempF;
  // G61
  const operationUpperLowerTandemVolumeForExpansionGal = !upperTandemSeal
    ? 0
    : (lowerTandemSeal?.bag_chambers || 0) > 0
      ? 0
      : upperTandemSeal?.volume_for_bag_expansion_upper_tandem_gallons;
  // H61
  const operationUpperLowerTandemAmountOfExpansionGal =
    operationUpperLowerTandemVolumeForExpansionGal *
    operationUpperLowerTandemTempDeltaF *
    motorOilCoeff;
  // F62
  const operationLowerLowerTandemTempDeltaF =
    motorSkinTempF - installLowerLowerTandemTempDeltaF - surfaceTempF;
  // G62
  const operationLowerLowerTandemVolumeForExpansionGal =
    lowerTandemSeal?.volume_for_bag_expansion_lower_tandem_gallons || 0;
  // H62
  const operationLowerLowerTandemAmountOfExpansionGal =
    operationLowerLowerTandemVolumeForExpansionGal *
    operationLowerLowerTandemTempDeltaF *
    motorOilCoeff;
  // I63
  const operationLowerTandemTotalSealExpansionGal =
    operationUpperLowerTandemVolumeForExpansionGal +
    operationLowerLowerTandemVolumeForExpansionGal;
  // F63
  const operationLowerTandemMotorTempDeltaF =
    motorWindingTempF - (installLowerTandemMotorTempDeltaF + surfaceTempF);
  // G63
  const operationLowerTandemMotorVolumeForExpansionGal =
    installLowerTandemMotorVolumeForExpansionGal;
  // H63
  const operationLowerTandemMotorAmountOfExpansionGal =
    operationLowerTandemMotorVolumeForExpansionGal *
    operationLowerTandemMotorTempDeltaF *
    motorOilCoeff;
  // H64
  const operationLowerTandemTotalAmountOfExpansionGal =
    operationUpperLowerTandemAmountOfExpansionGal +
    operationLowerLowerTandemAmountOfExpansionGal +
    operationLowerTandemMotorAmountOfExpansionGal;
  // I64
  const operationLowerTandemTotalExpansionGal =
    operationLowerTandemTotalAmountOfExpansionGal >
    operationLowerTandemTotalSealExpansionGal
      ? operationLowerTandemTotalSealExpansionGal
      : operationLowerTandemTotalAmountOfExpansionGal;
  // J64
  const operationLowerTandemExpansionRatio =
    operationLowerTandemTotalAmountOfExpansionGal /
    operationLowerTandemTotalSealExpansionGal;

  // PULL
  // If no upper seal then these are 0
  // F68
  const pullUpperLowerTandemTempDeltaF = installUpperLowerTandemTempDeltaF;
  // G68
  const pullUpperLowerTandemVolumeForExpansionGal = !upperTandemSeal
    ? 0
    : (lowerTandemSeal?.bag_chambers || 0) > 0
      ? 0
      : upperTandemSeal?.volume_for_bag_expansion_upper_tandem_gallons;
  // H68
  const pullUpperLowerTandemAmountOfExpansionGal =
    pullUpperLowerTandemVolumeForExpansionGal *
    pullUpperLowerTandemTempDeltaF *
    motorOilCoeff;
  // F69
  const pullLowerLowerTandemTempDeltaF = installLowerLowerTandemTempDeltaF;
  // G69
  const pullLowerLowerTandemVolumeForExpansionGal =
    lowerTandemSeal?.volume_for_bag_expansion_lower_tandem_gallons || 0;
  // H69
  const pullLowerLowerTandemAmountOfExpansionGal =
    pullLowerLowerTandemVolumeForExpansionGal *
    pullLowerLowerTandemTempDeltaF *
    motorOilCoeff;
  // G70
  const pullLowerTandemTotalSealExpansionGal =
    pullUpperLowerTandemVolumeForExpansionGal +
    pullLowerLowerTandemVolumeForExpansionGal;
  // F70
  const pullLowerTandemMotorTempDeltaF = motorWindingTempF - surfaceTempF;
  // G70
  const pullLowerTandemMotorVolumeForExpansionGal =
    operationLowerTandemMotorVolumeForExpansionGal;
  // H70
  const pullLowerTandemMotorAmountOfExpansionGal =
    pullLowerTandemMotorVolumeForExpansionGal *
    pullLowerTandemMotorTempDeltaF *
    motorOilCoeff;
  // H71
  const pullLowerTandemTotalAmountOfExpansionGal =
    pullUpperLowerTandemAmountOfExpansionGal +
    pullLowerLowerTandemAmountOfExpansionGal +
    pullLowerTandemMotorAmountOfExpansionGal;
  // I70
  const pullLowerTandemTotalExpansionBeforeCapGal =
    pullLowerTandemTotalSealExpansionGal -
    operationLowerTandemTotalExpansionGal;
  // I71
  const pullLowerTandemTotalExpansionGal =
    pullLowerTandemTotalAmountOfExpansionGal >
    pullLowerTandemTotalExpansionBeforeCapGal
      ? pullLowerTandemTotalExpansionBeforeCapGal
      : pullLowerTandemTotalAmountOfExpansionGal;
  // J71
  const pullLowerTandemExpansionRatio =
    pullLowerTandemTotalAmountOfExpansionGal /
    pullLowerTandemTotalExpansionBeforeCapGal;

  // Get the upper chamber for top most seal
  // These upper chamber calcs will always be used no matter if there are
  // 1 or 2 seals. On the sheet they are on the upper tandem table but
  // it should be on the lower tandem table
  const lowerTandemUpperChamberPullVolumeForExpansionGal = upperTandemSeal
    ? upperTandemSeal?.upper_chamber_pull_volume_gallons || 0
    : lowerTandemSeal?.upper_chamber_pull_volume_gallons || 0;

  const lowerTandemUpperChamberPullTotalExpansionBeforeCapGal =
    operationLowerTandemTotalExpansionGal + pullLowerTandemTotalExpansionGal;

  const lowerTandemUpperChamberPullTotalExpansionGal =
    lowerTandemUpperChamberPullTotalExpansionBeforeCapGal >
    lowerTandemUpperChamberPullVolumeForExpansionGal
      ? lowerTandemUpperChamberPullVolumeForExpansionGal
      : lowerTandemUpperChamberPullTotalExpansionBeforeCapGal;

  const lowerTandemUpperChamberPullExpansionRatio =
    lowerTandemUpperChamberPullTotalExpansionGal /
    lowerTandemUpperChamberPullVolumeForExpansionGal;

  // H75
  const lowerTandemTotalContractionVolumeGal =
    pullLowerTandemTotalAmountOfExpansionGal +
    operationLowerTandemTotalAmountOfExpansionGal;

  return {
    install: {
      upper: {
        depthFt: topSealDepthFt,
        tempDeltaF: installUpperLowerTandemTempDeltaF,
        volumeForExpansionGal: installUpperLowerTandemVolumeForExpansionGal,
        amountOfExpansionGal: installUpperLowerTandemAmountOfExpansionGal
      },
      lower: {
        depthFt: lowerSealDepthFt,
        tempDeltaF: installLowerLowerTandemTempDeltaF,
        volumeForExpansionGal: installLowerLowerTandemVolumeForExpansionGal,
        amountOfExpansionGal: installLowerLowerTandemAmountOfExpansionGal
      },
      motor: {
        depthFt: motorDepthFt,
        tempDeltaF: installLowerTandemMotorTempDeltaF,
        volumeForExpansionGal: installLowerTandemMotorVolumeForExpansionGal,
        amountOfExpansionGal: installLowerTandemMotorAmountOfExpansionGal
      },
      totalSealExpansionGal: installLowerTandemTotalSealExpansionGal,
      totalAmountOfExpansionGal: installLowerTandemTotalAmountOfExpansionGal,
      totalExpansionGal: installLowerTandemTotalExpansionGal,
      expansionRatio: installLowerTandemExpansionRatio
    },
    operation: {
      upper: {
        depthFt: topSealDepthFt,
        tempDeltaF: operationUpperLowerTandemTempDeltaF,
        volumeForExpansionGal: operationUpperLowerTandemVolumeForExpansionGal,
        amountOfExpansionGal: operationUpperLowerTandemAmountOfExpansionGal
      },
      lower: {
        depthFt: lowerSealDepthFt,
        tempDeltaF: operationLowerLowerTandemTempDeltaF,
        volumeForExpansionGal: operationLowerLowerTandemVolumeForExpansionGal,
        amountOfExpansionGal: operationLowerLowerTandemAmountOfExpansionGal
      },
      motor: {
        depthFt: motorDepthFt,
        tempDeltaF: operationLowerTandemMotorTempDeltaF,
        volumeForExpansionGal: operationLowerTandemMotorVolumeForExpansionGal,
        amountOfExpansionGal: operationLowerTandemMotorAmountOfExpansionGal
      },
      totalSealExpansionGal: operationLowerTandemTotalSealExpansionGal,
      totalAmountOfExpansionGal: operationLowerTandemTotalAmountOfExpansionGal,
      totalExpansionGal: operationLowerTandemTotalExpansionGal,
      expansionRatio: operationLowerTandemExpansionRatio
    },
    pull: {
      upper: {
        depthFt: topSealDepthFt,
        tempDeltaF: pullUpperLowerTandemTempDeltaF,
        volumeForExpansionGal: pullUpperLowerTandemVolumeForExpansionGal,
        amountOfExpansionGal: pullUpperLowerTandemAmountOfExpansionGal
      },
      lower: {
        depthFt: lowerSealDepthFt,
        tempDeltaF: pullLowerLowerTandemTempDeltaF,
        volumeForExpansionGal: pullLowerLowerTandemVolumeForExpansionGal,
        amountOfExpansionGal: pullLowerLowerTandemAmountOfExpansionGal
      },
      motor: {
        depthFt: motorDepthFt,
        tempDeltaF: pullLowerTandemMotorTempDeltaF,
        volumeForExpansionGal: pullLowerTandemMotorVolumeForExpansionGal,
        amountOfExpansionGal: pullLowerTandemMotorAmountOfExpansionGal
      },
      totalSealExpansionGal: pullLowerTandemTotalExpansionBeforeCapGal,
      totalAmountOfExpansionGal: pullLowerTandemTotalAmountOfExpansionGal,
      totalExpansionGal: pullLowerTandemTotalExpansionGal,
      expansionRatio: pullLowerTandemExpansionRatio,
      upperChamberExpansionDuringPull:
        lowerTandemUpperChamberPullTotalExpansionGal,
      upperChamberPullExpansionRatio: lowerTandemUpperChamberPullExpansionRatio
    },
    totalContractionVolumeGal: lowerTandemTotalContractionVolumeGal
  };
}

export function getUpperTandemSealData(
  seal: Maybe<Partial<Seals>>,
  motorOilCoeff: number,
  gradientFPerFt: number,
  topSealDepthFt: number,
  motorSkinTempF: number,
  surfaceTempF: number,
  installLowerTandemTotalExpansionGal: number,
  operationLowerTandemTotalExpansionGal: number,
  pullLowerTandemTotalExpansionGal: number,
  lowerTandemTotalContractionVolumeGal: number
): SealExpansionReturn {
  // INSTALL
  // Q54
  const installUpperUpperTandemTempDeltaF = topSealDepthFt * gradientFPerFt;
  // R54
  const installUpperUpperTandemVolumeForExpansionGal =
    seal?.volume_for_bag_expansion_upper_tandem_gallons || 0;
  // S54
  const installUpperUpperTandemAmountOfExpansionGal =
    installUpperUpperTandemVolumeForExpansionGal *
    installUpperUpperTandemTempDeltaF *
    motorOilCoeff;
  // S57
  const installUpperUpperTandemTotalAmountOfExpansionGal =
    installUpperUpperTandemAmountOfExpansionGal +
    installLowerTandemTotalExpansionGal;
  // T57
  const installUpperUpperTandemTotalExpansionGal =
    installUpperUpperTandemTotalAmountOfExpansionGal >
    installUpperUpperTandemVolumeForExpansionGal
      ? installUpperUpperTandemVolumeForExpansionGal
      : installUpperUpperTandemTotalAmountOfExpansionGal;
  // U57
  const installUpperUpperTandemExpansionRatio =
    installUpperUpperTandemTotalAmountOfExpansionGal /
    installUpperUpperTandemVolumeForExpansionGal;

  // OPERATION
  // Q61
  const operationUpperUpperTandemTempDeltaF =
    motorSkinTempF - (installUpperUpperTandemTempDeltaF + surfaceTempF);
  // R61
  const operationUpperUpperTandemVolumeForExpansionGal =
    seal?.volume_for_bag_expansion_upper_tandem_gallons || 0;
  // S61
  const operationUpperUpperTandemAmountOfExpansionGal =
    operationUpperUpperTandemVolumeForExpansionGal *
    operationUpperUpperTandemTempDeltaF *
    motorOilCoeff;
  // S64
  const operationUpperUpperTandemTotalAmountOfExpansionGal =
    operationUpperUpperTandemAmountOfExpansionGal +
    operationLowerTandemTotalExpansionGal;
  // T64
  const operationUpperTandemTotalExpansionGal =
    operationUpperUpperTandemTotalAmountOfExpansionGal >
    operationUpperUpperTandemVolumeForExpansionGal
      ? operationUpperUpperTandemVolumeForExpansionGal
      : operationUpperUpperTandemTotalAmountOfExpansionGal;
  // U64
  const operationUpperTandemExpansionRatio =
    operationUpperUpperTandemTotalAmountOfExpansionGal /
    operationUpperUpperTandemVolumeForExpansionGal;

  // PULL
  // Q68
  const pullUpperUpperTandemTempDeltaF = motorSkinTempF - surfaceTempF;
  // R68
  const pullUpperUpperTandemVolumeForExpansionGal =
    seal?.volume_for_bag_expansion_upper_tandem_gallons || 0;
  // S68
  const pullUpperUpperTandemAmountOfExpansionGal =
    pullUpperUpperTandemVolumeForExpansionGal *
    pullUpperUpperTandemTempDeltaF *
    motorOilCoeff;
  // S71
  const pullUpperUpperTandemTotalAmountOfExpansionGal =
    pullUpperUpperTandemAmountOfExpansionGal + pullLowerTandemTotalExpansionGal;
  // T70
  const pullUpperTandemTotalSealExpansionBeforeCapGal =
    pullUpperUpperTandemVolumeForExpansionGal -
    operationUpperTandemTotalExpansionGal;
  // T71
  const pullUpperTandemTotalExpansionGal =
    pullUpperUpperTandemTotalAmountOfExpansionGal >
    pullUpperTandemTotalSealExpansionBeforeCapGal
      ? pullUpperTandemTotalSealExpansionBeforeCapGal
      : pullUpperUpperTandemTotalAmountOfExpansionGal;
  // U71
  const pullUpperTandemExpansionRatio =
    pullUpperUpperTandemTotalAmountOfExpansionGal /
    pullUpperTandemTotalSealExpansionBeforeCapGal;

  // These are never going to be used even though they're in the spreadsheet
  // in the upper tandem table. THey have been added to the lower tandem calcs
  // here and those will always be used.
  // R73
  const upperTandemUpperChamberPullVolumeForExpansionGal =
    seal?.upper_chamber_pull_volume_gallons || 0;
  // T73
  const upperTandemUpperChamberPullTotalExpansionBeforeCapGal =
    operationUpperTandemTotalExpansionGal + pullUpperTandemTotalExpansionGal;
  // T74
  const upperTandemUpperChamberPullTotalExpansionGal =
    upperTandemUpperChamberPullTotalExpansionBeforeCapGal >
    upperTandemUpperChamberPullVolumeForExpansionGal
      ? upperTandemUpperChamberPullVolumeForExpansionGal
      : upperTandemUpperChamberPullTotalExpansionBeforeCapGal;
  // U74
  const upperTandemUpperChamberPullExpansionRatio =
    upperTandemUpperChamberPullTotalExpansionGal /
    upperTandemUpperChamberPullVolumeForExpansionGal;
  // S75
  const upperTandemTotalContractionVolumeGal =
    pullUpperUpperTandemTotalAmountOfExpansionGal +
    operationUpperUpperTandemTotalAmountOfExpansionGal -
    lowerTandemTotalContractionVolumeGal;
  return {
    install: {
      upper: {
        depthFt: topSealDepthFt,
        tempDeltaF: installUpperUpperTandemTempDeltaF,
        volumeForExpansionGal: installUpperUpperTandemVolumeForExpansionGal,
        amountOfExpansionGal: installUpperUpperTandemAmountOfExpansionGal
      },
      totalSealExpansionGal: installUpperUpperTandemVolumeForExpansionGal,
      totalAmountOfExpansionGal:
        installUpperUpperTandemTotalAmountOfExpansionGal,
      totalExpansionGal: installUpperUpperTandemTotalExpansionGal,
      expansionRatio: installUpperUpperTandemExpansionRatio
    },
    operation: {
      upper: {
        depthFt: topSealDepthFt,
        tempDeltaF: operationUpperUpperTandemTempDeltaF,
        volumeForExpansionGal: operationUpperUpperTandemVolumeForExpansionGal,
        amountOfExpansionGal: operationUpperUpperTandemAmountOfExpansionGal
      },
      totalSealExpansionGal: operationUpperUpperTandemVolumeForExpansionGal,
      totalAmountOfExpansionGal:
        operationUpperUpperTandemTotalAmountOfExpansionGal,
      totalExpansionGal: operationUpperTandemTotalExpansionGal,
      expansionRatio: operationUpperTandemExpansionRatio
    },
    pull: {
      upper: {
        depthFt: topSealDepthFt,
        tempDeltaF: pullUpperUpperTandemTempDeltaF,
        volumeForExpansionGal: pullUpperUpperTandemVolumeForExpansionGal,
        amountOfExpansionGal: pullUpperUpperTandemAmountOfExpansionGal
      },
      totalSealExpansionGal: pullUpperTandemTotalSealExpansionBeforeCapGal,
      totalAmountOfExpansionGal: pullUpperUpperTandemTotalAmountOfExpansionGal,
      totalExpansionGal: pullUpperTandemTotalExpansionGal,
      expansionRatio: pullUpperTandemExpansionRatio,
      upperChamberExpansionDuringPull:
        upperTandemUpperChamberPullTotalExpansionGal,
      upperChamberPullExpansionRatio: upperTandemUpperChamberPullExpansionRatio
    },
    totalContractionVolumeGal: upperTandemTotalContractionVolumeGal
  };
}
